import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, ShieldCheck, ArrowLeft, ArrowUpRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { api } from '@/api';
import { TID } from '@/testIds';

const BIZ_TYPES = [
  'Print Shop', 'Sign Company', 'Marketing Agency',
  'Dealer / Fleet Operator', 'Detail Shop', 'Signage Manufacturer', 'Other',
];

const VOLUMES = [
  { id: '1-5',   label: '1–5 wraps/mo' },
  { id: '6-15',  label: '6–15 wraps/mo' },
  { id: '16-40', label: '16–40 wraps/mo' },
  { id: '41-100',label: '41–100 wraps/mo' },
  { id: '100+',  label: '100+ / high-volume' },
];

const SERVICES = [
  'Vehicle wraps (full)', 'Fleet livery', 'Color-change restyling', 'Partial / accent packages',
  'Wall / floor / window graphics', 'Cut vinyl / door logos', 'Wrap removal', 'Aftercare subscription (MRR)',
];

const MEDIA = ['3M 2080', '3M IJ180C', 'Avery MPI 1105', 'Avery SW900', 'Orafol 970RA', 'Hexis Skintac', 'Other / TBD'];

export default function PartnerApplyPage() {
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', role: '',
    business_type: 'Print Shop', website: '', city: '', state: '',
    monthly_wrap_volume: '6-15', prints_in_house: true,
    media_used: [], services_interested: [], notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const toggleArr = (k, v) => setForm({ ...form, [k]: form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v] });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k];
      });
      const { data } = await api.post('/partners/apply', payload);
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Please check the form and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <div className="grid lg:grid-cols-[1fr_1.35fr] gap-10">
            {/* Left column: pitch */}
            <div>
              <div className="mi-eyebrow mb-3 text-[#E63946]">// B2B PARTNER PROGRAM</div>
              <h1 className="mi-headline text-5xl md:text-6xl mb-6">
                Add an install <span className="text-[#E63946]">department</span> to your shop — overnight.
              </h1>
              <p className="text-white/75 leading-relaxed mb-6">
                Print, drop-ship, invoice. Motor iNKS is your seamless, blind-shipping backend fulfillment team.
                Apply below and we'll send your custom rate sheet + onboarding kit within one business day.
              </p>

              <div className="mi-surface p-5 mb-5">
                <div className="mi-eyebrow mb-3 text-[#E63946]">// PROFIT BLUEPRINT · HOUSTON METRO</div>
                <div className="space-y-2 text-sm text-white/85 font-mono-data">
                  <Row k="Retail sell (extended van)"     v="$3,500 – $4,200" />
                  <Row k="Your print cost (cast + lam)"   v="~$750" />
                  <Row k="Our wholesale labor (FLT-LG)"   v="$1,400 – $1,800" />
                  <div className="mi-sep my-2" />
                  <Row k="Net cost to shop"               v="~$1,800 – $2,150" />
                  <Row k="Pure profit / vehicle"          v="$1,350 – $2,050" strong />
                </div>
              </div>

              <ul className="space-y-3 text-sm text-white/80">
                <Bullet>Unbranded, blind-shipped — your invoice, your brand, your client.</Bullet>
                <Bullet>Zero-failure Master Execution Guarantee on every install.</Bullet>
                <Bullet>Automatic bulk-tier deductions past 1,500 sqft / contract.</Bullet>
                <Bullet>MRR aftercare program lets you auto-bill retail every month.</Bullet>
              </ul>

              <Link to="/rates" className="inline-flex items-center gap-2 mt-6 text-[#E63946] hover:text-white transition-colors">
                See the full rate card <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Right column: form or success */}
            {done ? (
              <div className="mi-surface p-8 lg:p-10 self-start">
                <ShieldCheck className="w-12 h-12 text-[#E63946] mb-4" />
                <div className="mi-headline text-4xl mb-3">Application received.</div>
                <p className="text-white/75 leading-relaxed mb-6">
                  We'll follow up within one business day with your custom rate sheet, onboarding kit and a direct line
                  to your account lead. Meanwhile, browse the public rate card or run some numbers on the Wrapulator.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/rates" className="mi-btn-red">View rate card <ArrowUpRight className="w-4 h-4" /></Link>
                  <Link to="/#wrapulator" className="mi-btn-ghost">Try wholesale mode</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mi-surface p-6 lg:p-8">
                <div className="mi-eyebrow mb-4">// PARTNER APPLICATION</div>

                <Fieldset label="Company">
                  <Input required placeholder="Company / DBA" value={form.company_name} onChange={upd('company_name')} testid={TID.partnerCompany} />
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Input required placeholder="Your name" value={form.contact_name} onChange={upd('contact_name')} testid={TID.partnerContact} />
                    <Input placeholder="Your role (optional)" value={form.role} onChange={upd('role')} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Input required type="email" placeholder="Work email" value={form.email} onChange={upd('email')} testid={TID.partnerEmail} />
                    <Input placeholder="Phone" value={form.phone} onChange={upd('phone')} testid={TID.partnerPhone} />
                  </div>
                  <Input className="mt-3" placeholder="Website (optional)" value={form.website} onChange={upd('website')} testid={TID.partnerWebsite} />
                </Fieldset>

                <Fieldset label="Business">
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      required data-testid={TID.partnerBizType}
                      value={form.business_type} onChange={upd('business_type')}
                      className="bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm"
                    >
                      {BIZ_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <select
                      required data-testid={TID.partnerVolume}
                      value={form.monthly_wrap_volume} onChange={upd('monthly_wrap_volume')}
                      className="bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm"
                    >
                      {VOLUMES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Input placeholder="City" value={form.city} onChange={upd('city')} testid={TID.partnerCity} />
                    <Input placeholder="State" value={form.state} onChange={upd('state')} testid={TID.partnerState} />
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-white/75">
                    <input type="checkbox" checked={form.prints_in_house}
                           onChange={(e) => setForm({ ...form, prints_in_house: e.target.checked })}
                           className="accent-[#E63946]" />
                    Prints in-house (vs. outsourced printing)
                  </label>
                </Fieldset>

                <Fieldset label="Media used (multi)">
                  <ChipGrid items={MEDIA} selected={form.media_used} onToggle={(v) => toggleArr('media_used', v)} />
                </Fieldset>

                <Fieldset label="Services you'd like to enable">
                  <ChipGrid items={SERVICES} selected={form.services_interested} onToggle={(v) => toggleArr('services_interested', v)} />
                </Fieldset>

                <Fieldset label="Notes">
                  <textarea
                    data-testid={TID.partnerNotes}
                    placeholder="Anything specific — timelines, geographic areas, existing pain points…"
                    value={form.notes} onChange={upd('notes')} rows={3}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder-white/40"
                  />
                </Fieldset>

                <div className="mt-4 mi-eyebrow text-white/50 text-[10px]">
                  // SUBMISSION SENT TO SALES@MOTORINKS.COM
                </div>
                <button
                  type="submit" disabled={submitting}
                  data-testid={TID.partnerSubmit}
                  className="mi-btn-red mt-3 w-full disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : (<>Submit partner application <Send className="w-4 h-4" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Fieldset({ label, children }) {
  return (
    <div className="mb-5">
      <div className="mi-eyebrow mb-2 text-white/70">{label}</div>
      {children}
    </div>
  );
}
function Input({ testid, className = '', ...props }) {
  return (
    <input
      data-testid={testid} {...props}
      className={`w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25 ${className}`}
    />
  );
}
function ChipGrid({ items, selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const active = selected.includes(it);
        return (
          <button type="button" key={it} onClick={() => onToggle(it)}
                  className={`px-3 py-1.5 border text-[12px] transition-colors ${
                    active ? 'border-[#E63946] bg-[#E63946]/15 text-white' : 'border-white/10 text-white/65 hover:border-white/30'
                  }`}>
            {it}
          </button>
        );
      })}
    </div>
  );
}
function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="text-[#E63946] mt-0.5">→</span>
      <span>{children}</span>
    </li>
  );
}
function Row({ k, v, strong }) {
  return (
    <div className={`flex items-center justify-between ${strong ? 'text-white font-bold' : ''}`}>
      <span className={strong ? '' : 'text-white/60'}>{k}</span>
      <span>{v}</span>
    </div>
  );
}
